import {
  formatDate,
  Frequency,
  getDayjs,
  GroupDescriptionType,
  InputDataDetailsType,
  InputDataSubjectDetailsUrl,
  ISO_DATE_FORMAT,
  OutputVersionType,
  OutputSnapshotVersionType,
  PeriodEndPicker,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { Flex, Select } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataSubjectHeader.translations.json';
import React, { useCallback, useMemo } from 'react';
import { Dayjs } from 'dayjs';
import {
  InputDataDescriptionInfo,
  InputDataVersionInfo,
  InputDataVersionType,
} from './types';
import { InputDataStatusSelect } from './InputDataStatusSelect';
import { expand } from 'uri-template-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInputDataDetailsStoreValue } from './InputDataDetailsContext';
import { AppLink } from '../../../common/AppLink';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { useInputDataActions } from './useInputDataActions';
import { Helmet } from 'react-helmet-async';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-sample-header',
);
addTranslations(translations);

export interface InputDataSampleHeaderProps {
  data?: InputDataDetailsType;
  descriptionData: InputDataDescriptionInfo;
  selectedSubmittedDataId?: number;
  onSelectSubmittedDataId?: (submittedDataId: number) => void;
  output?: OutputSnapshotVersionType;
  versionData: InputDataVersionInfo;
  onReload: () => Promise<any>;
  isLoading: boolean;
}

function InputDataBreadcrumb({
  data,
  description,
}: {
  data?: InputDataDetailsType;
  description?: GroupDescriptionType;
}) {
  const { t } = useTranslation();
  const title =
    data?.loadingStatus === 'NO_SAMPLE'
      ? '...'
      : `${description?.code} - ${formatDate(data?.valueDate)} - ${
          data?.dataSubjectCode
        }`;

  return (
    <span>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <AppLink id="input-data-directory" to="/data/input-data">
        {t('input-data')}
      </AppLink>
      {` / ${title}`}
    </span>
  );
}

interface DescriptionSelectProps {
  descriptionId?: number | null;
  descriptionOptions: DefaultOptionType[];
  disabled: boolean;
  hasDescriptions?: boolean;
  onSelect: (id: number | null | undefined) => void;
}

function InputDescriptionSelect(props: DescriptionSelectProps) {
  if (!props.hasDescriptions) {
    return null;
  }

  return (
    <Select
      disabled={props.disabled}
      styles={{
        popup: { root: { width: '340px', maxHeight: 400 } },
      }}
      style={{ width: '355px' }}
      id={'description-select'}
      options={props.descriptionOptions}
      value={props.descriptionOptions.length ? props.descriptionId : undefined}
      onSelect={props.onSelect}
    />
  );
}

interface OutputVersionSelectProps {
  onSelectOutputVersion?: (submittedDataId: number) => void;
  submittedDataId?: number | null;
  outputVersion?: OutputVersionType;
}

function OutputVersionSelect({
  onSelectOutputVersion,
  submittedDataId,
  outputVersion,
}: OutputVersionSelectProps) {
  const options = useMemo(
    () =>
      outputVersion?.inputSources.map((source) => ({
        label: `${source.code} - ${source.name}`,
        value: source.submittedData.id,
      })) ?? [],
    [outputVersion?.inputSources],
  );

  if (!outputVersion || !submittedDataId) {
    return null;
  }

  return (
    <Select
      disabled={(options?.length ?? 0) < 2}
      styles={{
        popup: { root: { width: '340px', maxHeight: 400 } },
      }}
      style={{ width: '355px' }}
      id={'description-select'}
      options={options}
      value={submittedDataId}
      onSelect={onSelectOutputVersion}
    />
  );
}

export function InputDataSubjectHeader({
  data,
  descriptionData,
  selectedSubmittedDataId,
  onSelectSubmittedDataId,
  output,
  versionData,
  onReload,
  isLoading: isLoadingData,
}: InputDataSampleHeaderProps) {
  const [mode] = useInputDataDetailsStoreValue('mode');
  const [, setShowOutputs] = useInputDataDetailsStoreValue('showOutputs');
  const [, setSearchParams] = useSearchParams();
  const editing = mode === 'edit';
  const disabled = data?.loadingStatus === 'NO_SAMPLE';
  const navigate = useNavigate();

  const descriptionId = descriptionData.description?.id;
  const detailParams = useDetailParams(descriptionId);
  const { isLoading: isLoadingAction } = useInputDataActions(onReload);
  const isLoading = isLoadingAction || isLoadingData;

  const frequency = data?.frequency?.code as Frequency;

  const { setDescription } = descriptionData;
  const selectedDate = useMemo(
    () => (data?.valueDate && getDayjs(data.valueDate)) || undefined,
    [data?.valueDate],
  );

  const onSelect = useCallback(
    (id: number | null | undefined) => {
      let description: GroupDescriptionType | undefined;
      if (id) {
        description = data?.descriptions?.find((object) => object.id === id);
      }

      setDescription(description);
      if (description?.id) {
        const descriptionId = description.id.toString();
        setSearchParams((params) => {
          const newParams = new URLSearchParams(params);
          newParams.set('descriptionId', descriptionId);
          return newParams;
        });
      }
    },
    [data?.descriptions, setDescription, setSearchParams],
  );

  const onChangeStatus = useCallback(
    (version: InputDataVersionType) => {
      if (version === 'output') {
        // Show outputs selection dialog on each click on 'output-based' option
        setShowOutputs(true);
        return;
      }

      const suitableDescriptions = new Set(
        data?.descriptions
          ?.filter((description) =>
            description.versions?.some((descriptionVersion) => {
              if (version === 'active') {
                return descriptionVersion.activeVersion;
              }
              if (version === 'draft') {
                return descriptionVersion.draftVersion;
              }
              return false;
            }),
          )
          .map((description) => description.id),
      );
      if (
        descriptionData.description?.id &&
        !suitableDescriptions.has(descriptionData.description.id)
      ) {
        const firstSuitable = suitableDescriptions.values().next()?.value;
        onSelect(firstSuitable);
      }
    },
    [
      data?.descriptions,
      descriptionData.description?.id,
      onSelect,
      setShowOutputs,
    ],
  );

  const samplePeriods = useMemo(
    () =>
      data?.samplePeriods?.map((period) => ({
        from: getDayjs(period.from),
        to: getDayjs(period.to),
      })) ?? [],
    [data?.samplePeriods],
  );
  const isDisabledDate = useCallback(
    (date: Dayjs) =>
      !samplePeriods.some(({ from, to }) =>
        date.isBetween(from, to, 'day', '[]'),
      ),
    [samplePeriods],
  );
  const changePeriod = useCallback(
    (date: Dayjs | null) => {
      if (!date) {
        return;
      }
      navigate(
        expand(InputDataSubjectDetailsUrl, {
          ...detailParams,
          date: date.format(ISO_DATE_FORMAT),
        }),
      );
    },
    [detailParams, navigate],
  );

  return (
    <Flex gap="middle" align="center">
      <InputDataBreadcrumb
        data={data}
        description={descriptionData.description}
      />
      <div>
        <PeriodEndPicker
          allowClear={false}
          value={selectedDate}
          disabled={editing || isLoading}
          disabledDate={isDisabledDate}
          onChange={changePeriod}
          frequency={frequency}
        />
      </div>
      <InputDataStatusSelect
        disabled={disabled || editing || isLoading}
        versionData={versionData}
        onChange={onChangeStatus}
      />
      {versionData.version === 'output' ? (
        <OutputVersionSelect
          submittedDataId={selectedSubmittedDataId}
          onSelectOutputVersion={onSelectSubmittedDataId}
          outputVersion={output?.outputVersion}
        />
      ) : (
        <InputDescriptionSelect
          descriptionOptions={descriptionData.descriptionOptions}
          descriptionId={descriptionData.description?.id}
          disabled={
            disabled || editing || !data?.dataObjectGroupId || isLoading
          }
          hasDescriptions={!!data?.descriptions}
          onSelect={onSelect}
        />
      )}
    </Flex>
  );
}

function useDetailParams(descriptionId?: number | null) {
  const [{ descriptionId: queryDescriptionId, groupId, subject }] =
    useQueryParams();

  return useMemo(
    () => ({
      descriptionId: descriptionId ?? queryDescriptionId,
      groupId,
      subject,
    }),
    [descriptionId, groupId, queryDescriptionId, subject],
  );
}
